import { useState, useEffect } from "react";

export const FriendEducationPage = ({ data }) => {
  const [educationList, setEducationList] = useState([]);
  const [selectedEducation, setSelectedEducation] = useState(null);
  const [openCourse, setOpenCourse] = useState(true);
  const [openAddress, setOpenAddress] = useState(true);

  const courseFieldKeys = new Set([
    "edu_institution_name",
    "education_level",
    "course_name",
    "edu_start_date",
    "edu_end_date",
    "edu_duration_of_program",
    "edu_program_delivery",
    "edu_pin_code",
    "edu_city_region_country",
    "education_current_studying",
  ]);

  const addressFieldKeys = new Set([
    "edu_address_building_number",
    "edu_address_building_name",
    "edu_address_street_name",
    "edu_address_pincode",
    "edu_address_google_map",
    "edu_address_website",
    "edu_contact_number",
  ]);

  const fieldConfig = [
    { key: "education_current_studying", label: "Currently Studying" },
    { key: "edu_institution_name", label: "Name of the Institution", icon: "../input-logos/user.svg" },
    { key: "education_level", label: "Education Level" },
    { key: "course_name", label: "Course Name", icon: "../input-logos/user.svg" },
    { key: "edu_start_date", label: "Start Date" },
    { key: "edu_end_date", label: "End Date" },
    { key: "edu_duration_of_program", label: "Duration of Program" },
    { key: "edu_program_delivery", label: "Program Delivery" },
    { key: "edu_pin_code", label: "Pincode" },
    { key: "edu_city_region_country", label: "City, Region and Country" },
    { key: "edu_address_building_number", label: "Building Number/Office Number" },
    { key: "edu_address_building_name", label: "Building Name" },
    { key: "edu_address_street_name", label: "Street Name" },
    { key: "edu_address_pincode", label: "Pincode" },
    { key: "edu_address_google_map", label: "Address" },
    { key: "edu_address_website", label: "Website" },
    { key: "edu_contact_number", label: "Contact Number" },
    
  ];

  const getFieldValue = (edu, key) => {
    const value = edu?.[key];
    if (value === undefined || value === null) return "";
    return String(value).trim() === "" ? "" : value;
  };

  const isPlusCodePart = (value = "") =>
    /^[A-Z0-9]{4,}\+[A-Z0-9]{2,}/i.test(String(value || "").trim());

  const cleanMapDetail = (value = "") => {
    const text = String(value || "").trim();
    return isPlusCodePart(text) ? "" : text;
  };

  const getPreviewIcon = (key) => {
    if (["edu_start_date", "edu_end_date", "education_current_studying", "edu_duration_of_program"].includes(key)) return "../input-logos/calander.svg";
    if (["edu_pin_code", "edu_address_pincode", "edu_city_region_country", "edu_address_google_map"].includes(key)) return "../input-logos/location.svg";
    if (["edu_address_building_number", "edu_address_building_name", "edu_address_street_name"].includes(key)) return "../input-logos/building.svg";
    return "../input-logos/user.svg";
  };

  const renderPreviewFieldCard = (field) => {
    const value = String(field.value || "").trim();
    if (!value) return null;

    return (
      <div
        key={field.key}
        className={`${field.key === "edu_address_google_map" ? "min-[1100px]:col-span-2" : ""} flex items-center gap-3 px-4 py-4 border-b last:border-b-0`}
      >
        <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 text-[#6938EF]">
          <img src={field.icon || getPreviewIcon(field.key)} alt="" className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-xs text-gray-500">{field.label}</div>
          <div className="text-sm font-medium text-gray-900 break-words">{value || "—"}</div>
        </div>
      </div>
    );
  };

  const renderCurrentStatusPreview = (label) => (
    <div className="min-[1100px]:col-span-2 px-4 py-4 border-b last:border-b-0">
      <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
        <span className="text-sm font-semibold text-gray-900">{label}</span>
        <span className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#6938EF]">
          <span className="absolute right-1 h-4 w-4 rounded-full bg-white shadow-sm" />
        </span>
      </div>
    </div>
  );

  const formatEducationAddress = (edu) => {
    const addressParts = [
      cleanMapDetail(edu?.edu_address_building_number),
      cleanMapDetail(edu?.edu_address_building_name),
      cleanMapDetail(edu?.edu_address_street_name),
      edu?.edu_address_city,
      edu?.edu_address_region,
      edu?.edu_address_country,
      edu?.edu_address_pincode || edu?.edu_pin_code,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    if (addressParts.length > 0) {
      return addressParts.join(", ");
    }

    return getFieldValue(edu, "edu_city_region_country") || getFieldValue(edu, "edu_address_google_map");
  };

  const formatStudyPeriod = (edu) => {
    const startRaw = String(edu?.edu_start_date || "").trim();
    const endRaw = String(edu?.edu_end_date || "").trim();
    const parseMonth = (value) => {
      if (!value) return null;
      if (/^\d{4}-\d{2}$/.test(value)) {
        const [year, month] = value.split("-");
        return { year: Number(year), month: Number(month) };
      }
      if (/^\d{2}\/\d{4}$/.test(value)) {
        const [month, year] = value.split("/");
        return { year: Number(year), month: Number(month) };
      }
      return null;
    };

    const monthNamesAbbrev = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const start = parseMonth(startRaw);
    const end = parseMonth(endRaw);

    const fmt = (d) => {
      if (!d) return "";
      const m = Number(d.month);
      const y = Number(d.year);
      if (Number.isNaN(m) || Number.isNaN(y)) return "";
      const name = monthNamesAbbrev[(m - 1) % 12] || "";
      return `${name} ${y}`;
    };

    if (start && end) {
      const startText = fmt(start);
      const endText = fmt(end);
      if (startText && endText) return `${startText} - ${endText}`;
    }

    const durationValue = String(edu?.edu_duration_of_program || "").trim();
    if (durationValue) return durationValue;

    if (Number(edu?.education_current_studying) === 1 && start) {
      const startText = fmt(start);
      if (startText) return `${startText} - Present`;
    }

    if (start) return fmt(start) || "";
    if (end) return fmt(end) || "";
    return "";
  };

  const getDetailsFields = (edu) => {
    return fieldConfig
      .map((field) => ({
        ...field,
        value:
          field.key === "edu_address_pincode"
            ? getFieldValue(edu, "edu_address_pincode") || getFieldValue(edu, "edu_pin_code")
            : getFieldValue(edu, field.key),
      }))
      .filter((field) => String(field.value || "").trim() !== "");
  };

  const getSummaryValues = (edu) => {
    return {
      courseName: getFieldValue(edu, "course_name"),
      institutionName: getFieldValue(edu, "edu_institution_name"),
      instituteAddress: formatEducationAddress(edu),
      studyPeriod: formatStudyPeriod(edu),
      educationLevel: getFieldValue(edu, "education_level"),
    };
  };

  // ✅ Load from parent prop (visible_profile.education)
  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      setEducationList(data);
    }
  }, [data]);

  // ✅ Card click → show full details
  const handleCardClick = (edu) => {
    setSelectedEducation(edu);
    setOpenCourse(true);
    setOpenAddress(true);
  };

  // ✅ Back button → go back to card list
  const handleBack = () => {
    setSelectedEducation(null);
  };

  // ✅ Toggle current-study locally (UI-only)
  // (UI-only toggles removed — not used in current UI)

  const selectedDetails = selectedEducation ? getDetailsFields(selectedEducation) : [];
  const selectedCourseDetails = selectedDetails.filter((field) => courseFieldKeys.has(field.key));
  const selectedAddressDetails = selectedDetails.filter((field) => addressFieldKeys.has(field.key));
  const displayEducationList = educationList.filter((edu) => {
    const summary = getSummaryValues(edu);
    return Boolean(
      String(summary.courseName || "").trim() ||
      String(summary.institutionName || "").trim() ||
      String(summary.instituteAddress || "").trim() ||
      String(summary.studyPeriod || "").trim()
    ) && !(
      !String(summary.courseName || "").trim() &&
      !String(summary.institutionName || "").trim() &&
      !String(summary.instituteAddress || "").trim() &&
      String(summary.studyPeriod || "").trim() === "Present"
    );
  });


  return (
    <>
      {selectedEducation ? (
        // 🔹 FULL DETAIL VIEW (Same input style)
        <div>
          <div className="rounded-2xl p-4 min-[900px]:p-6 border border-gray-100 shadow-sm">
            <div className="flex justify-start items-center">
               <button
                aria-label={`Back to previous page`}
                 onClick={handleBack}
                className="flex items-center aoboshi-one text-lg text-[#212121] font-medium p-4 rounded-2xl"
              >
                <img src="../back-arrow.svg" alt="back-arrow" className="mr-4" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                Education Details
              </h2>
             
            </div>

            <div className="min-h-0  [&::-webkit-scrollbar]:hidden">
              <div className="mt-4 gap-4">
                {selectedCourseDetails.length > 0 && (
                <div className="mb-4 border border-gray-100 rounded-xl overflow-hidden bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenCourse((prev) => !prev)}
                    className={`w-full flex items-center justify-between px-5 py-4 transition-all ${
                      openCourse
                        ? "bg-gradient-to-r from-[#6938EF]/5 to-transparent border-b border-[#E6E2FF]"
                        : "bg-gradient-to-r from-gray-50 to-white"
                    }`}
                  >
                    <span className={`font-semibold transition-colors ${openCourse ? "text-[#6938EF]" : "text-gray-900"}`}>
                      Institution and Course Details
                    </span>
                    <span className={`transition-transform duration-300 ${openCourse ? "rotate-180 text-[#6938EF]" : "text-gray-600"}`}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>

                  {openCourse && (
                    <div className="grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 px-4 pb-4">
                      {selectedCourseDetails.map((field) => {
                        if (field.key === "education_current_studying") {
                          // only render the Currently Studying row when the value indicates current (1)
                          if (Number(selectedEducation?.education_current_studying) !== 1) return null;
                          return renderCurrentStatusPreview("Currently Studying");
                        }

                        return renderPreviewFieldCard(field);
                      })}
                    </div>
                  )}
                </div>
                )}

                {selectedAddressDetails.length > 0 && (
                  <div className="mb-4 border border-gray-100 rounded-xl overflow-hidden bg-white">
                    <button
                      type="button"
                      onClick={() => setOpenAddress((prev) => !prev)}
                      className={`w-full flex items-center justify-between px-5 py-4 transition-all ${
                        openAddress
                          ? "bg-gradient-to-r from-[#6938EF]/5 to-transparent border-b border-[#E6E2FF]"
                          : "bg-gradient-to-r from-gray-50 to-white"
                      }`}
                    >
                      <span className={`font-semibold transition-colors ${openAddress ? "text-[#6938EF]" : "text-gray-900"}`}>
                        Institution Address
                      </span>
                      <span className={`transition-transform duration-300 ${openAddress ? "rotate-180 text-[#6938EF]" : "text-gray-600"}`}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </button>

                    {openAddress && (
                      <div className="grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 px-4 pb-4">
                        {selectedAddressDetails.map(renderPreviewFieldCard)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // 🔹 CARD LIST VIEW (YOUR DESIGN PRESERVED)
        <div className="rounded-2xl p-4 min-[900px]:p-6 border border-gray-100 shadow-sm ">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Education</h2>
          </div>

          <div className="space-y-6 min-h-0 py-5">
            {displayEducationList.length > 0 ? (
              displayEducationList.map((edu, index) => {
                const summary = getSummaryValues(edu);
                return (
                <div
                  key={edu.education_id || index}
                  onClick={() => handleCardClick(edu)}
                  className="relative rounded-2xl border-gray-200 p-8  bg-white transition"
                >
                

                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-4 min-w-0">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] text-white p-3">
                          <svg fill="#fff" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" stroke="#fff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M197.769 791.767l60.672-286.853c2.341-11.066-4.733-21.934-15.799-24.275s-21.934 4.733-24.275 15.799l-60.672 286.853c-2.341 11.066 4.733 21.934 15.799 24.275s21.934-4.733 24.275-15.799zm571.063-286.786l61.778 287.068c2.38 11.058 13.273 18.093 24.33 15.713s18.093-13.273 15.713-24.33l-61.778-287.068c-2.38-11.058-13.273-18.093-24.33-15.713s-18.093 13.273-15.713 24.33z"></path><path d="M967.45 386.902L535.9 208.126c-10.609-4.399-30.569-4.442-41.207-.088L57.821 386.901l436.881 178.857c10.624 4.355 30.583 4.313 41.207-.085L967.45 386.901zM551.583 603.516c-20.609 8.533-51.787 8.599-72.409.145L24.437 417.494c-32.587-13.359-32.587-47.847.009-61.188l454.73-186.174c20.641-8.448 51.818-8.382 72.407.156l448.836 185.936c32.466 13.442 32.466 47.913.004 61.354l-448.84 185.938zm288.673 166.569c-98 57.565-209.669 88.356-325.888 88.356-116.363 0-228.162-30.866-326.246-88.564-9.749-5.735-22.301-2.481-28.036 7.268s-2.481 22.301 7.268 28.036c104.336 61.377 223.297 94.22 347.014 94.22 123.564 0 242.386-32.763 346.634-93.998 9.753-5.729 13.015-18.279 7.286-28.032s-18.279-13.015-28.032-7.286z"></path><path d="M983.919 383.052v296.233c0 11.311 9.169 20.48 20.48 20.48s20.48-9.169 20.48-20.48V383.052c0-11.311-9.169-20.48-20.48-20.48s-20.48 9.169-20.48 20.48z"></path></g></svg>
                          </div>
                        </div>

                        <div className="min-w-0">
                          <h3 className="text-lg font-bold text-gray-900 truncate">{summary.institutionName || "—"}</h3>
                          <p className="mt-1 text-sm text-gray-600 truncate flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            <span>{summary.instituteAddress || "—"}</span>
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2">
                 {edu.education_current_studying === "1" && (
                    <div className=" h-5 w-5 sm:h-6 sm:w-6 top-2 sm:top-3 left-2 sm:left-3 flex justify-center items-center bg-[#6938EF] rounded-[8px] sm:rounded-[12px] p-1 shadow-sm">
                      <svg fill="#ffffff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff">
                        <g id="SVGRepo_iconCarrier">
                          <path d="M16.948 20.2a1 1 0 0 1-.465-.115l-4.472-2.352-4.471 2.348a1 1 0 0 1-1.451-1.054l.854-4.979-3.619-3.527a1 1 0 0 1 .554-1.706l5-.727 2.237-4.531A1 1 0 0 1 12.011 3a1 1 0 0 1 .9.558l2.236 4.53 5 .727a1 1 0 0 1 .554 1.706l-3.621 3.526.854 4.98a1 1 0 0 1-.986 1.173zM6.171 10.5l2.544 2.479a1 1 0 0 1 .285.888l-.6 3.5 3.144-1.653a.993.993 0 0 1 .931 0l3.144 1.653-.6-3.5a1 1 0 0 1 .288-.885l2.544-2.482-3.515-.511a1 1 0 0 1-.752-.547l-1.573-3.183-1.573 3.186a1 1 0 0 1-.752.547z" />
                        </g>
                      </svg>
                    </div>
                  )}         
                      </div>
                    </div>

                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-gray-700">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Period</span>
                        <span className="font-medium mt-1 text-gray-900">{summary.studyPeriod || "—"}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Course</span>
                        <span className="font-medium mt-1 text-gray-900">{summary.courseName || "—"}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Education Level</span>
                        <span className="font-medium mt-1 text-gray-900">{summary.educationLevel || "—"}</span>
                      </div>
                    </div>                   
                  </div>
                </div>
              );
              })
              ) : (
              <div className="flex flex-col h-[70dvh] justify-center items-center">
                <img src="../no-data/no-edu.svg" alt="No data" className="mx-auto mb-4 h-40 w-40" />
                <p className="font-semibold text-[28px] text-center">No education details added yet.</p>
                <p className="text-gray-500 text-center">This user hasn't added education details yet.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
